import { Avatar, Box, Group, Menu, Text, UnstyledButton } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Code2, LogOut, UserCog } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthUser, useHasRole } from '../providers/AuthProvider';
import { performLogout } from '../services/userManager';
import {
  isDebugEnabled,
  setDebugEnabled,
  useDebugEnabled,
} from '../utils/debugFlag';

const getInitials = (firstName?: string | null, lastName?: string | null) => {
  const first = firstName?.trim().charAt(0) ?? '';
  const last = lastName?.trim().charAt(0) ?? '';
  const initials = `${first}${last}`.toUpperCase();
  return initials || '?';
};

export function AppHeaderUserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthUser();
  const isAdmin = useHasRole('admin');
  const debugEnabled = useDebugEnabled();

  const displayName =
    `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() ||
    user?.full_name?.trim() ||
    user?.email ||
    t('header_user_profile', 'Impostazioni account');

  const profileLabel =
    user?.type === 'enterprise'
      ? t('header_user_profile_enterprise', 'Profilo organizzazione')
      : t('header_user_profile', 'Impostazioni account');

  const handleLogout = async () => {
    const serverLogoutSuccess = await performLogout();
    navigate('/login');

    if (!serverLogoutSuccess) {
      notifications.show({
        color: 'orange',
        withBorder: true,
        radius: 'lg',
        title: 'Warning',
        autoClose: 5000,
        message: 'Logged out locally, but server logout failed',
      });
    }
  };

  const handleToggleDeveloperMode = () => {
    const next = !isDebugEnabled();
    setDebugEnabled(next);
    notifications.show({
      color: next ? 'teal' : 'gray',
      title: next
        ? 'Modalità sviluppatore'
        : 'Modalità sviluppatore disattivata',
      message: next
        ? 'Attiva. Developer Panel disponibile su tutte le pagine.'
        : 'Disattivata.',
      autoClose: 4000,
    });
  };

  return (
    <Menu shadow='md' width={260} position='bottom-end'>
      <Menu.Target>
        <UnstyledButton
          aria-label={profileLabel}
          style={{
            borderRadius: 'var(--mantine-radius-md)',
            padding: '4px 8px',
          }}
        >
          <Group gap='sm' wrap='nowrap'>
            <Avatar
              src={user?.profile_photo_path ?? undefined}
              radius='xl'
              size={40}
            >
              {getInitials(user?.first_name, user?.last_name)}
            </Avatar>
            <Box visibleFrom='md'>
              <Text size='sm' fw={600} lh={1.2} lineClamp={1}>
                {displayName}
              </Text>
              <Text size='xs' c='dimmed' lh={1.2} lineClamp={1}>
                {user?.email}
              </Text>
            </Box>
          </Group>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>{user?.email}</Menu.Label>
        <Menu.Item
          component={Link}
          to='/profile'
          leftSection={<UserCog size={16} />}
        >
          {profileLabel}
        </Menu.Item>
        {isAdmin && (
          <>
            <Menu.Divider />
            <Menu.Item
              leftSection={<Code2 size={16} />}
              onClick={handleToggleDeveloperMode}
            >
              {debugEnabled
                ? t('header_exit_developer_mode', 'Esci modalità sviluppatore')
                : t(
                    'header_enter_developer_mode',
                    'Entra modalità sviluppatore'
                  )}
            </Menu.Item>
          </>
        )}
        <Menu.Divider />
        <Menu.Item
          color='red'
          leftSection={<LogOut size={16} />}
          onClick={() => {
            void handleLogout();
          }}
        >
          {t('header_logout', 'Logout')}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
